import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from "@/components/layout/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Utensils, Plane } from "lucide-react";

const Travel = () => {
  const [activeTab, setActiveTab] = useState("restaurants");

  // Mock data for restaurants
  const restaurants = [
    {
      id: 1,
      name: "라스베가스 한식당",
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=500&fit=crop",
      category: "한식",
      rating: 4.8,
      reviews: 128,
      location: "스트립 중심부, 라스베가스",
      description: "정통 한국 음식을 맛볼 수 있는 라스베가스 최고의 한식당",
      badges: ["인기", "가족 친화적", "예약 필수"]
    },
    {
      id: 2,
      name: "베가스 스시",
      image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=800&h=500&fit=crop",
      category: "일식",
      rating: 4.5,
      reviews: 95,
      location: "차이나타운, 라스베가스",
      description: "신선한 해산물로 만든 정통 일식 스시와 사시미",
      badges: ["프리미엄", "데이트 코스"]
    },
    {
      id: 3,
      name: "비스타 카페",
      image: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800&h=500&fit=crop",
      category: "카페",
      rating: 4.2,
      reviews: 67,
      location: "서머린, 라스베가스",
      description: "고급 커피와 베이커리가 맛있는 아늑한 카페",
      badges: ["아침식사", "커피 명소"]
    },
    {
      id: 4,
      name: "불고기 하우스",
      image: "https://images.unsplash.com/photo-1529193591184-b1d58069ecdd?w=800&h=500&fit=crop",
      category: "한식",
      rating: 4.6,
      reviews: 112,
      location: "스프링 밸리, 라스베가스",
      description: "가족 경영 불고기 전문점, 20년 이상의 전통",
      badges: ["전통", "가족 경영"]
    }
  ];

  // Mock data for travel destinations
  const destinations = [
    {
      id: 1,
      name: "그랜드 캐니언",
      image: "https://images.unsplash.com/photo-1472396961693-142e6e269027?w=800&h=500&fit=crop",
      category: "자연 명소",
      rating: 4.9,
      reviews: 312,
      location: "애리조나",
      description: "세계에서 가장 유명한 자연 명소, 라스베가스에서 당일치기 가능",
      badges: ["인기", "필수 방문"]
    },
    {
      id: 2,
      name: "레드 락 캐니언",
      image: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=500&fit=crop",
      category: "자연 명소",
      rating: 4.7,
      reviews: 189,
      location: "라스베가스 근교",
      description: "붉은 바위와 아름다운 풍경이 있는 자연 보호구역",
      badges: ["하이킹", "사진 명소"]
    },
    {
      id: 3,
      name: "후버 댐",
      image: "https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=800&h=500&fit=crop",
      category: "명소",
      rating: 4.5,
      reviews: 224,
      location: "네바다/애리조나 경계",
      description: "미국의 엔지니어링 경이로움, 웅장한 수력발전 댐",
      badges: ["역사적", "투어 가능"]
    },
    {
      id: 4,
      name: "밸리 오브 파이어",
      image: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=500&fit=crop",
      category: "자연 명소",
      rating: 4.6,
      reviews: 156,
      location: "네바다",
      description: "붉은 사암 지형이 만들어낸 화성 같은 풍경의 주립공원",
      badges: ["사막", "일출 명소"]
    }
  ];

  return (
    <Layout>
      <div className="container py-8">
        <h1 className="text-3xl font-bold mb-2">여행정보</h1>
        <p className="text-muted-foreground mb-6">
          라스베가스와 주변 지역의 최고 맛집과 여행지를 찾아보세요
        </p>

        <div className="mb-8">
          <Tabs defaultValue="restaurants" onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="restaurants" className="flex items-center gap-2">
                <Utensils className="h-4 w-4" />
                맛집
              </TabsTrigger>
              <TabsTrigger value="destinations" className="flex items-center gap-2">
                <Plane className="h-4 w-4" />
                여행지
              </TabsTrigger>
            </TabsList>

            <TabsContent value="restaurants">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {restaurants.map((restaurant) => (
                  <Link 
                    to={`/restaurant/${restaurant.id}`} 
                    key={restaurant.id}
                  >
                    <Card className="overflow-hidden transition-all hover:shadow-lg">
                      <div 
                        className="h-48 bg-cover bg-center relative" 
                        style={{ backgroundImage: `url(${restaurant.image})` }}
                      >
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                          <Badge className="bg-primary text-white">{restaurant.category}</Badge>
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold text-lg mb-1">{restaurant.name}</h3>
                        <div className="flex items-center gap-1 text-amber-500 mb-2">
                          <Star className="h-4 w-4 fill-current" />
                          <span className="font-medium">{restaurant.rating}</span>
                          <span className="text-muted-foreground text-sm">({restaurant.reviews})</span>
                        </div>
                        <div className="flex items-start gap-1 text-muted-foreground mb-3">
                          <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                          <span className="text-sm">{restaurant.location}</span>
                        </div>
                        <p className="text-sm mb-4 line-clamp-2">{restaurant.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {restaurant.badges.map((badge, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {badge}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="destinations">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {destinations.map((destination) => (
                  <Link 
                    to={`/destination/${destination.id}`} 
                    key={destination.id}
                  >
                    <Card className="overflow-hidden transition-all hover:shadow-lg">
                      <div 
                        className="h-48 bg-cover bg-center relative" 
                        style={{ backgroundImage: `url(${destination.image})` }}
                      >
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                          <Badge className="bg-primary text-white">{destination.category}</Badge>
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold text-lg mb-1">{destination.name}</h3>
                        <div className="flex items-center gap-1 text-amber-500 mb-2">
                          <Star className="h-4 w-4 fill-current" />
                          <span className="font-medium">{destination.rating}</span>
                          <span className="text-muted-foreground text-sm">({destination.reviews})</span>
                        </div>
                        <div className="flex items-start gap-1 text-muted-foreground mb-3">
                          <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                          <span className="text-sm">{destination.location}</span>
                        </div>
                        <p className="text-sm mb-4 line-clamp-2">{destination.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {destination.badges.map((badge, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {badge}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default Travel;
